import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { CourseWithFullDetails } from '@/lib/types';
import { sortFocusSkillsByOrder } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export type ReportCourse = CourseWithFullDetails;

export function useReportCourses() {
	return useQuery<ReportCourse[]>({
		queryKey: ['get-report-courses', schoolId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('courses')
				.select(
					`
					course_name,
					course_id,
					course_description,
					course_resource_info,
					course_prereq,
					course_extras,
					course_discipline,
					course_division,
					grade,
					last_updated,
					is_ap,
					is_archived,
					is_saved_draft,
					courses_disciplines (
						discipline:disciplines (
							name,
							id
						)
					),
					courses_divisions (
						division:divisions (
							name,
							id
						)
					),
					courses_outcomes (
						courses_outcomes_id,
						outcome:outcomes (
							outcome_order,
							outcome_text,
							outcome_id
						)
					),
					courses_resources (
						course_resources_id,
						resource:resources (
							resource_author,
							resource_detail,
							resource_isbn,
							resource_id,
							resource_title,
							resource_type
						)
					),
					courses_users (
						user_id,
						course_id,
						user:users (
							user_id,
							user_first,
							user_last
						)
					),
					courses_focuses (
						courses_focuses_id,
						focus:focuses (
							focus_title,
							focus_type,
							focus_order,
							focus_id,
							focuses_skills (
								focuses_skills_id,
								skill_order,
								skill:skills (
									skill_id,
									skill_text,
									skill_type
								)
							)
						)
					)
				`
				)
				.eq('school_id', schoolId)
				.order('course_name', { ascending: true });

			if (error) throw error;

			for (const course of data ?? []) {
				sortFocusSkillsByOrder(course.courses_focuses);
			}

			return (data ?? []) as unknown as ReportCourse[];
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}
